// Graph View - Fully customizable axis selection and visualization
// Now with complete Ragone Plot functionality + 3D capability
import { useEffect, useState } from 'preact/hooks'
import { trackPlotView } from '../../modules/dataService.js'
import { resetAllFilters } from '../../state/filterStore.js'
import { filteredCells, settingsVisible } from '../../state/globalSignals.js'
import { IconMaximize, IconMinimize, IconScatterPlot } from '../shared/Icons.jsx'
import FreePlotECharts from './FreePlotECharts.jsx'
import { FreePlotSettingsOverlay } from './FreePlotSettingsOverlay.jsx'

export default function FreePlotView() {
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Use filteredCells from global state (loaded by MainAppPage)
  const cellData = filteredCells.value || []
  // Track initial loading state - set to false after first render cycle
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Force complete chart remount when fullscreen or settings visibility changes (ensures proper sizing)
  const [chartKey, setChartKey] = useState(0)

  // Track plot view when component mounts
  useEffect(() => {
    trackPlotView('free_plot', 'explorer')
  }, [])

  useEffect(() => {
    // Wait for CSS transition to complete before remounting chart
    const timeout = setTimeout(() => {
      setChartKey(prev => prev + 1)
    }, 550) // After CSS transition (0.4s) + buffer

    return () => clearTimeout(timeout)
  }, [isFullscreen, settingsVisible.value])

  // Mark initial load as complete after a short delay
  // This ensures we show loading spinner briefly on first render,
  // but switch to empty state if no cells are found
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsInitialLoad(false)
    }, 500) // Give data time to load, then show empty state if needed

    return () => clearTimeout(timeout)
  }, [])

  // Also mark as loaded immediately if we have cells
  useEffect(() => {
    if (cellData.length > 0) {
      setIsInitialLoad(false)
    }
  }, [cellData.length])

  // Show loading only during initial load
  if (isInitialLoad && cellData.length === 0) {
    return (
      <div style="display:flex;align-items:center;justify-content:center;height:100%;color:#94a3b8;font-family:Roboto,sans-serif;">
        <div style="text-align:center;">
          <div style="width:48px;height:48px;border:4px solid rgba(234,91,12,0.2);border-top-color:#ea5b0c;border-radius:50%;margin:0 auto 16px;animation:spin 1s linear infinite;"></div>
          <div>Loading Cell Data...</div>
        </div>
      </div>
    )
  }

  // Show empty state when filters produce no results (after initial load)
  if (!isInitialLoad && cellData.length === 0) {
    return (
      <div className="empty-state-modern">
        <div className="empty-state-card">
          <div className="empty-state-icon-wrapper">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M8 12h8"></path>
              <path d="M12 8v8"></path>
            </svg>
          </div>
          <h3 className="empty-state-title">No Cells Found</h3>
          <p className="empty-state-description">
            No battery cells match your current filters.
            <br />
            Try adjusting or clearing some filters to see more results.
          </p>
          <button
            className="empty-state-action"
            onClick={() => resetAllFilters()}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18"></path>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            Clear All Filters
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`free-plot-container ${isFullscreen ? 'fullscreen plot-fullscreen' : ''}`} data-tour="graph-plot-area">
      {/* Main Plot Tile with Header */}
      <div className="free-plot-tile">
        <div className="free-plot-header">
          <div className="free-plot-title">
            <IconScatterPlot size={18} className="free-plot-icon" />
            <span className="free-plot-label">Graph</span>
            <span className="free-plot-count">{cellData.length} Cells</span>
          </div>

          {/* Maximize/Minimize Toggle */}
          <button
            className="free-plot-maximize-btn"
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen Mode'}
          >
            {isFullscreen ? <IconMinimize size={18} /> : <IconMaximize size={18} />}
          </button>
        </div>
        <div className="free-plot-chart" style={{ position: 'relative' }}>
          <FreePlotECharts key={chartKey} cells={cellData} />
        </div>
      </div>

      {/* Settings Drawer */}
      <FreePlotSettingsOverlay />
    </div>
  )
}
